import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Form, Alert, Modal, Spinner } from 'react-bootstrap';
import { IconPlus, IconTrash, IconQrcode } from '@tabler/icons-react';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import { api } from '../../../shared/api';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import NumberInput from '../../../components/form/NumberInput';
import Select, { components } from '../../../components/form/AppSelect';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import { tariffTypeHumanify } from '../utils/tariffType';
import { useConfirm } from '../../../shared/ConfirmContext';
import QRScannerModal from '../../warehouses/QRScannerModal';
import AddBundleModal from '../../warehouses/AddBundleModal';
import { useQuery, useQueryClient, useInfiniteQuery, useMutation } from '@tanstack/react-query';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Custom MenuList component for infinite scroll
const MenuList = (props) => {
  const { onLoadMore, hasMore, isLoading } = props.selectProps;
  const scrollElementRef = useRef(null);

  const handleScroll = useCallback((e) => {
    const element = e.target;
    const { scrollTop, scrollHeight, clientHeight } = element;

    // Check if scrolled to bottom (within 10px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      if (hasMore && !isLoading && onLoadMore) {
        onLoadMore();
      }
    }
  }, [hasMore, isLoading, onLoadMore]);

  // Use effect to add scroll listener to the menu list element
  useEffect(() => {
    const element = scrollElementRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        element.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <components.MenuList
      {...props}
      innerRef={scrollElementRef}
    >
      {props.children}
      {isLoading && (
        <div className="d-flex justify-content-center p-2">
          <Spinner size="sm" animation="border" />
          <span className="ms-2">Loading more items...</span>
        </div>
      )}
      {!hasMore && props.children && props.children.length > 0 && (
        <div className="text-center p-2 text-muted small">
          No more items to load
        </div>
      )}
    </components.MenuList>
  );
};

const QRScanButton = ({ rentalId }) => {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);
  return (
    <>
      <Button variant="outline-secondary" onClick={() => setShow(true)}>
        <IconQrcode size={16} className="me-1" />
        {t('warehouses.scanner.scanButton')}
      </Button>
      {show && (
        <QRScannerModal
          contextType="rental"
          contextId={rentalId}
          onClose={() => setShow(false)}
        />
      )}
    </>
  );
};

const AddBundleButton = ({ rental, onRefresh }) => {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);

  const handleAddItems = async (entries) => {
    for (const entry of entries) {
      await api.post(`rentals/api/rentals/${rental.id}/items/`, {
        stock_item: entry.stock_item_id,
        quantity: entry.quantity,
      });
    }
    if (onRefresh) onRefresh();
  };

  return (
    <>
      <Button variant="outline-primary" onClick={() => setShow(true)}>
        <IconPlus size={16} className="me-1" />
        {t('warehouses.bundles.addToProject')}
      </Button>
      <AddBundleModal show={show} onHide={() => setShow(false)} onAddItems={handleAddItems} />
    </>
  );
};

const RentalSettingsItems = ({ rental, setRental }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ item: '', quantity: 1 });
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [debouncedWarehouseSearch, setDebouncedWarehouseSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedWarehouseSearch(warehouseSearch), 300);
    return () => clearTimeout(timer);
  }, [warehouseSearch]);

  const { data: rentalItemsData, isLoading: tableLoading } = useQuery({
    queryKey: ['rental-items', rental.id],
    queryFn: async () => {
      const [rentalItemsResponse, warehousesResponse] = await Promise.all([
        api.get(`rentals/api/rentals/${rental.id}/items/`),
        api.get('warehouses/api/warehouses/'),
      ]);
      return { rentalItems: rentalItemsResponse, warehouses: warehousesResponse };
    },
  });

  useRealtimeEvents((e) => {
    if (e.model === 'rentals.rentalitem') {
      queryClient.invalidateQueries({ queryKey: ['rental-items', rental.id] });
    }
  });

  const items = rentalItemsData?.rentalItems ?? [];
  const warehouseOptions = (rentalItemsData?.warehouses ?? []).map(w => ({ value: w.id, label: w.name }));

  // Auto-select warehouse when only one exists
  useEffect(() => {
    if (warehouseOptions.length === 1 && !selectedWarehouse) {
      setSelectedWarehouse(warehouseOptions[0]);
    }
  }, [warehouseOptions]);

  const {
    data: itemsData,
    isLoading: itemsInitialLoading,
    isFetchingNextPage,
    hasNextPage: hasMoreItems,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['warehouse-items-for-rental', selectedWarehouse?.value, debouncedWarehouseSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ page: pageParam, ordering: 'name' });
      params.set('stock', String(selectedWarehouse.value));
      if (debouncedWarehouseSearch) params.append('search', debouncedWarehouseSearch);
      return api.getRaw(`warehouses/api/stock-items/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.next ? new URL(lastPage.next).searchParams.get('page') : undefined,
    enabled: !!selectedWarehouse,
  });

  const itemsLoading = itemsInitialLoading || isFetchingNextPage;
  const itemOptions = (itemsData?.pages.flatMap(p => p.results ?? p.items ?? []) ?? []).map(item => ({
    value: item.id,
    label: `${item.product_name} (${t('rentals.add.available')}: ${item.quantity_available})`,
    data: item,
  }));
  const totalItemCount = itemsData?.pages[0]?.count ?? 0;

  // Load more items for infinite scroll
  const loadMoreItems = useCallback(() => {
    if (selectedWarehouse && hasMoreItems && !itemsLoading) {
      fetchNextPage();
    }
  }, [selectedWarehouse, hasMoreItems, itemsLoading, fetchNextPage]);

  // Handle warehouse selection change
  const handleWarehouseChange = (selected) => {
    setSelectedWarehouse(selected);
    setWarehouseSearch('');
  };

  // Handle item search
  const handleItemSearch = (inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change' && selectedWarehouse) {
      setWarehouseSearch(inputValue);
    }
  };

  const addItemMutation = useMutation({
    mutationFn: (data) => api.post(`rentals/api/rentals/${rental.id}/items/`, data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['rental-items', rental.id] });
      setFormData({ stock_item: '', quantity: 1 });
      setShowAddItemModal(false);
      setSuccess(true);
      const rentalResponse = await api.get(`rentals/api/rentals/${rental.id}/`);
      setRental(rentalResponse);
    },
    onError: () => setError(t('rentals.settings.items.updateError')),
  });

  const removeItemMutation = useMutation({
    mutationFn: (itemId) => api.delete(`rentals/api/rentals/${rental.id}/items/${itemId}/`),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['rental-items', rental.id] });
      setSuccess(true);
      const rentalResponse = await api.get(`rentals/api/rentals/${rental.id}/`);
      setRental(rentalResponse);
    },
    onError: () => setError(t('rentals.settings.items.updateError')),
  });

  // Handle add item form submission
  const handleAddItem = (e) => {
    e.preventDefault();
    setError(null);
    addItemMutation.mutate({ stock_item: formData.stock_item, quantity: formData.quantity });
  };

  // Handle form field changes
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle item selection from dropdown
  const handleItemSelect = (selected) => {
    handleInputChange('stock_item', selected ? selected.value : '');
  };

  // Handle removing an item
  const handleRemoveItem = async (itemId) => {
    if (!await confirm(t('common.confirm'))) return;
    removeItemMutation.mutate(itemId);
  };

  return (
    <>
      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          {t('rentals.settings.items.updateSuccess')}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="card-title">{t('rentals.settings.items.title')}</h2>
        <div className="d-flex gap-2">
          <QRScanButton rentalId={rental?.id} />
          <AddBundleButton rental={rental} onRefresh={() => queryClient.invalidateQueries({ queryKey: ['rental-items', rental.id] })} />
          <Button variant="primary" onClick={() => setShowAddItemModal(true)}>
            <IconPlus size={16} className="me-2" /> {t('rentals.settings.items.addItems')}
          </Button>
        </div>
      </div>

      {tableLoading ? (
        <CenteredLoader />
      ) : items.length === 0 ? (
        <Alert variant="info">
          {t('rentals.settings.items.noItems')}
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>{t('rentals.settings.items.item')}</th>
              <th>{t('rentals.settings.items.quantity')}</th>
              <th>{t('rentals.settings.pricing.units')}</th>
              <th>{t('rentals.settings.pricing.unitType')}</th>
              <th>{t('rentals.settings.pricing.pricePerUnit')}</th>
              <th>{t('rentals.settings.pricing.subtotal')}</th>
              <th>{t('rentals.settings.items.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td>{item.stock_item_product_name || '-'}</td>
                <td>{item.quantity}</td>
                <td>{item.tariff_quantity || 1}</td>
                <td>{tariffTypeHumanify(item.tariff_type || 'day', t)}</td>
                <td>{formatCurrency(item.tariff_price || 0, { decimals: 0 })}</td>
                <td>{formatCurrency(item.total_price || (item.tariff_price || 0) * item.quantity, { decimals: 0 })}</td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    <IconTrash size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add Item Modal */}
      <Modal show={showAddItemModal} onHide={() => setShowAddItemModal(false)} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>{t('rentals.settings.items.addItems')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddItem}>
            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.items.selectWarehouse')}</Form.Label>
              <Select
                value={selectedWarehouse}
                onChange={handleWarehouseChange}
                options={warehouseOptions}
                isLoading={false}
                isClearable
                placeholder={t('rentals.settings.items.selectWarehouse')}
                theme={selectTheme}
                className="react-select-container"
                classNamePrefix="react-select"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.items.item')}</Form.Label>
              <Select
                options={itemOptions}
                onChange={handleItemSelect}
                onInputChange={handleItemSearch}
                isLoading={itemsLoading}
                isClearable
                isDisabled={!selectedWarehouse}
                placeholder={t('rentals.settings.items.searchItems')}
                theme={selectTheme}
                className="react-select-container"
                classNamePrefix="react-select"
                components={{ MenuList }}
                onLoadMore={loadMoreItems}
                hasMore={hasMoreItems}
                totalCount={totalItemCount}
                required
              />
              {selectedWarehouse && totalItemCount > 0 && (
                <Form.Text className="text-muted">
                  Showing {itemOptions.length} of {totalItemCount} items
                  {hasMoreItems && ' - scroll down to load more'}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.items.quantity')}</Form.Label>
              <NumberInput
                min={1}
                step="any"
                value={formData.quantity}
                onChange={(quantity) => handleInputChange('quantity', quantity)}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setShowAddItemModal(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={addItemMutation.isPending || !formData.stock_item}
              >
                {addItemMutation.isPending ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RentalSettingsItems;
